/**
 * WhoopContextCard - Reusable card displaying latest WHOOP metrics
 * For use on Sleep Hub and Longevity Hub pages
 * 
 * UI-only, read-only, calm descriptive tone
 * Uses cached WHOOP data hook for better performance
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Activity, Battery, Heart, Moon } from 'lucide-react';
import { useWhoopDataCached } from '@/hooks/useWhoopDataCached';
import { Skeleton } from '@/components/ui/skeleton';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface WhoopContextCardProps {
  variant?: 'compact' | 'detailed';
  showLink?: boolean;
}

export function WhoopContextCard({ variant = 'compact', showLink = true }: WhoopContextCardProps) {
  const { isLoading, isConnected, latestCompleteData, lastSync } = useWhoopDataCached(7);

  if (!isConnected) {
    return null; // Don't render if WHOOP not connected
  }

  if (isLoading) {
    return (
      <Card className="border-primary/20">
        <CardHeader className="pb-2">
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Skeleton className="h-12 w-20" />
            <Skeleton className="h-12 w-20" />
            <Skeleton className="h-12 w-20" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!latestCompleteData) {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Activity className="h-4 w-4 text-primary" />
            WHOOP Data
          </CardTitle>
          <CardDescription className="text-xs">
            No recent WHOOP data available. Sync your device to see metrics.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const getRecoveryDescription = (score: number | null) => {
    if (!score) return 'Not available';
    if (score >= 67) return 'Well recovered';
    if (score >= 34) return 'Moderately recovered';
    return 'Recovery may be needed';
  };

  const getRecoveryColor = (score: number | null) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 67) return 'text-accent';
    if (score >= 34) return 'text-primary';
    return 'text-secondary';
  };

  const displayDate = latestCompleteData.date === format(new Date(), 'yyyy-MM-dd')
    ? 'Today'
    : format(parseISO(latestCompleteData.date), 'MMM d');

  if (variant === 'compact') {
    return (
      <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
        <CardContent className="py-3 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <Battery className="h-4 w-4 text-primary" />
                <span className={`font-medium ${getRecoveryColor(latestCompleteData.recovery_score)}`}>
                  {latestCompleteData.recovery_score ? `${Math.round(latestCompleteData.recovery_score)}%` : '--'}
                </span>
                <span className="text-xs text-muted-foreground">Recovery</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Heart className="h-4 w-4 text-accent" />
                <span className="font-medium text-accent">
                  {latestCompleteData.hrv_score ? Math.round(latestCompleteData.hrv_score) : '--'}
                </span>
                <span className="text-xs text-muted-foreground">HRV</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Moon className="h-4 w-4 text-primary" />
                <span className="font-medium text-foreground">
                  {latestCompleteData.sleep_performance ? `${Math.round(latestCompleteData.sleep_performance)}%` : '--'}
                </span>
                <span className="text-xs text-muted-foreground">Sleep</span>
              </div>
            </div>
            {showLink && (
              <Link to="/whoop-dashboard">
                <Button variant="ghost" size="sm" className="text-xs h-7">
                  View Details
                </Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4 text-primary" />
            Your WHOOP Status
          </CardTitle>
          <Badge variant="outline" className="text-xs font-normal border-primary/30">
            {displayDate}
          </Badge>
        </div>
        <CardDescription className="text-xs">
          {getRecoveryDescription(latestCompleteData.recovery_score)}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4">
          {/* Recovery */}
          <div className="text-center p-3 rounded-lg bg-card/50 border border-border/30">
            <Battery className={`h-5 w-5 mx-auto mb-1 ${getRecoveryColor(latestCompleteData.recovery_score)}`} />
            <p className={`text-xl font-semibold ${getRecoveryColor(latestCompleteData.recovery_score)}`}>
              {latestCompleteData.recovery_score ? `${Math.round(latestCompleteData.recovery_score)}%` : '--'}
            </p>
            <p className="text-xs text-muted-foreground">Recovery</p>
          </div>

          {/* HRV */}
          <div className="text-center p-3 rounded-lg bg-card/50 border border-border/30">
            <Heart className="h-5 w-5 mx-auto mb-1 text-accent" />
            <p className="text-xl font-semibold text-accent">
              {latestCompleteData.hrv_score ? Math.round(latestCompleteData.hrv_score) : '--'}
            </p>
            <p className="text-xs text-muted-foreground">HRV (ms)</p>
          </div>

          {/* Sleep Performance */}
          <div className="text-center p-3 rounded-lg bg-card/50 border border-border/30">
            <Moon className="h-5 w-5 mx-auto mb-1 text-primary" />
            <p className="text-xl font-semibold text-foreground">
              {latestCompleteData.sleep_performance ? `${Math.round(latestCompleteData.sleep_performance)}%` : '--'}
            </p>
            <p className="text-xs text-muted-foreground">Sleep</p>
          </div>
        </div>

        {showLink && (
          <div className="mt-4 pt-3 border-t border-border/30">
            <Link to="/whoop-dashboard">
              <Button variant="outline" size="sm" className="w-full text-sm">
                Open WHOOP Dashboard
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default WhoopContextCard;
